import { useStyles } from './styles/SystemFilters.styles';
import { MODEL_FILTER_OPTIONS, SystemStatus, SystemFilterOption, SystemAssigned } from '@consts/systems';

import type { SystemFilter } from '@/types/systems';
import { MultiSelectFilter } from '@components/MultiSelectFilter';
import { Typography, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';
import { useAppVersionsQuery } from '@features/redux/apis/system';

const statusOptions = Object.keys(SystemStatus);

interface SystemTableFiltersProps {
    filters: SystemFilter;
    setFilter: (key: string, value: string, selectOne?: boolean) => void;
    setAssigned: (value: SystemAssigned) => void;
    searchTerm: string;
    assigned: SystemAssigned;
}

export const SystemTableFilters = ({
    filters,
    setFilter,
    assigned,
    setAssigned,
    searchTerm,
}: SystemTableFiltersProps) => {
    const classes = useStyles();

    const { role } = useSelector((state: RootState) => state.auth.Roles);
    // TODO(re-enable 2026-05-11): App Version filter is temporarily hidden below;
    // keep the query alive so re-enabling is a single-line uncomment.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data: appVersionOptions = [] } = useAppVersionsQuery({});

    return (
        <>
            <MultiSelectFilter
                label={'Model'}
                options={MODEL_FILTER_OPTIONS}
                idKey={'id'}
                value={filters.model}
                onChange={(value: { id: string; label: string }) =>
                    setFilter(SystemFilterOption.Model, value.id, true)
                }
                getLabelValue={(option) => option.label}
                disabled={Boolean(searchTerm)}
            />

            <MultiSelectFilter
                search
                label={'Status'}
                options={statusOptions}
                value={filters.status}
                onChange={(value) => setFilter(SystemFilterOption.Status, value)}
                getLabelValue={(option) => option}
                disabled={Boolean(searchTerm)}
            />

            {/* TODO(re-enable 2026-05-11): App Version filter temporarily hidden. Re-enable by uncommenting. */}
            {/* <MultiSelectFilter
                label={'App Version'}
                options={appVersionOptions}
                value={filters.appVersion}
                onChange={(value) => setFilter(SystemFilterOption.AppVersion, value)}
                getLabelValue={(option) => option}
                disabled={Boolean(searchTerm)}
            /> */}

            {role.isAdmin && role.stores.edit && (
                <Box sx={{ mt: 1 }}>
                    <Typography
                        component='label'
                        sx={{
                            display: 'block',
                            fontSize: typographyTokens.fontSize.sm,
                            fontWeight: typographyTokens.fontWeight.medium,
                            color: colors.neutral[500],
                            mb: '6px',
                        }}>
                        Assignment
                    </Typography>
                    <ToggleButtonGroup
                        value={assigned}
                        exclusive
                        onChange={(_, value) => {
                            if (value !== null) {
                                setAssigned(value);
                            }
                        }}
                        disabled={Boolean(searchTerm)}
                        size='small'
                        sx={{
                            width: '100%',
                            backgroundColor: colors.neutral[100],
                            borderRadius: `${radii.md}px`,
                            '& .MuiToggleButton-root': {
                                flex: 1,
                                border: 'none',
                                borderRadius: `${radii.md}px !important`,
                                textTransform: 'none',
                                fontSize: typographyTokens.fontSize.sm,
                                fontWeight: 500,
                                color: colors.neutral[600],
                                py: 0.75,
                                '&:hover': {
                                    backgroundColor: colors.neutral[200],
                                },
                                '&.Mui-selected': {
                                    backgroundColor: colors.primary[500],
                                    color: colors.neutral[0],
                                    '&:hover': {
                                        backgroundColor: colors.primary[600],
                                    },
                                },
                                '&.Mui-disabled': {
                                    color: colors.neutral[400],
                                },
                            },
                        }}>
                        <ToggleButton value={SystemAssigned.Assigned}>Assigned</ToggleButton>
                        <ToggleButton value={SystemAssigned.Unassigned}>Unassigned</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}
        </>
    );
};
